<script setup>
// Hub aprovado, com gradiente apenas no atendimento.
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
  useWorkspaceNavigation,
  orderWorkspaceItems,
} from 'dashboard/composables/useWorkspaceNavigation';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { workspaceV2Path } from 'dashboard/helper/workspaceV2';
import { emitter } from 'shared/helpers/mitt';
import { useHubWorlds } from 'dashboard/routes/dashboard/hub/useHubWorlds';
import { matchHubDestinations } from 'dashboard/routes/dashboard/hub/hubSearch';
import HubAiCenterCard from './HubAiCenterCard.vue';
import AvisoUpgrade from 'dashboard/components-next/Planos/AvisoUpgrade.vue';
import {
  useHubOverview,
  EMPTY,
} from 'dashboard/routes/dashboard/hub/useHubOverview';
import 'dashboard/routes/dashboard/hub/hubDesign.css';

const props = defineProps({
  visibleWorlds: { type: Array, required: true },
  liveMetaByWorld: { type: Object, default: () => ({}) },
});
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { order } = useWorkspaceNavigation();
const { isAdmin } = useAdmin();
const overview = useHubOverview();
// Busca do topo: o mapa inteiro, inclusive Medir e Configurar, que não têm
// card na Central e antes só apareciam pela lupa da barra. Bloco da #1017.
const { allWorlds } = useHubWorlds();

const worlds = computed(() =>
  orderWorkspaceItems(props.visibleWorlds, order.value)
);
const worldById = id => worlds.value.find(world => world.id === id) || null;
const accountId = computed(() => String(route.params.accountId || ''));
const areaPath = id =>
  workspaceV2Path(`/app/accounts/${accountId.value}/hub/${id}`);
const routePath = (name, query = {}) => {
  const resolved = router.resolve({
    name,
    params: { accountId: accountId.value },
    query,
  });
  return workspaceV2Path(resolved.fullPath);
};

const count = value => (value === null || value === undefined ? EMPTY : value);
// Sem permissão para o dado (agente comum), o card mostra o que a área é em
// vez de um "·" que parece número quebrado.
const canSee = computed(() => overview.access.value);
const worldDescription = id => {
  const key = worldById(id)?.descriptionKey;
  return key ? t(key) : '';
};
const openOrganize = () => emitter.emit('workspace:organize:open');
const workSurfacePath = worldId => {
  const destination = worldById(worldId)?.primaryDestination;
  if (destination?.to) {
    return workspaceV2Path(router.resolve(destination.to).fullPath);
  }
  return worldId === 'sales' ? routePath('kanban_board') : routePath('home');
};

const SEARCH_LIMIT = 6;
const query = ref('');
const searchTerm = computed(() => query.value.trim());
const destinationHits = computed(() =>
  searchTerm.value
    ? matchHubDestinations(allWorlds.value, searchTerm.value, t).slice(
        0,
        SEARCH_LIMIT
      )
    : []
);
const destinationPath = destination =>
  workspaceV2Path(router.resolve(destination.to).fullPath);
// Enter vai para a busca do sistema (conversas, mensagens, contatos, artigos
// e cartões), que lê o termo do `?q=`.
const contentSearchPath = computed(() =>
  routePath('search', { q: searchTerm.value })
);
const submitSearch = () => {
  if (!searchTerm.value) return;
  router.push(contentSearchPath.value);
};
</script>

<template>
  <div
    class="hub-central hub-v3 flex-1 overflow-y-auto !bg-n-background px-5 py-7 !text-n-slate-12 md:px-10 md:py-9 [&_.hub-panel]:!min-h-[220px] [&_.hub-panel]:!gap-5 [&_.hub-panel]:!rounded-2xl [&_.hub-panel]:!border-n-weak [&_.hub-panel]:!bg-n-solid-1 [&_.hub-panel]:!px-6 [&_.hub-panel]:!py-5 [&_.hub-panel]:!shadow-none [&_.hub-panel]:before:!hidden [&_.hub-panel:hover]:!transform-none [&_.hub-panel-tag]:!text-n-slate-12 [&_.hub-panel-tag]:!normal-case [&_.hub-panel-tag]:!tracking-normal [&_.hub-panel-tag]:!text-[13px] [&_.hub-panel-ctx]:!text-n-slate-11 [&_.hub-panel-big]:!text-[38px] [&_.hub-panel-big]:!font-bold [&_.hub-panel-sub]:!text-lg [&_.hub-panel-go]:!text-n-blue-9 [&_.hub-panel-pill]:!bg-n-alpha-2 [&_.hub-panel-pill]:!text-n-slate-11 [&_.hub-cta--glow]:!bg-n-blue-9 [&_.hub-cta--glow]:!text-white [&_.hub-cta]:!shadow-none [&_.hub-cta]:!rounded-xl"
    data-testid="workspace-v2-hub"
  >
    <div class="mx-auto max-w-[1180px]">
      <div class="relative mb-5">
        <form role="search" @submit.prevent="submitSearch">
          <label
            data-testid="hub-search"
            class="flex min-h-12 w-full cursor-text items-center gap-3 rounded-2xl border border-n-weak bg-n-solid-1 px-4 focus-within:border-n-brand"
          >
            <span
              class="i-ph-magnifying-glass size-5 shrink-0 text-n-slate-10"
              aria-hidden="true"
            />
            <input
              v-model="query"
              type="search"
              autocomplete="off"
              class="reset-base !m-0 min-w-0 flex-1 !border-0 !bg-transparent !p-0 text-sm font-medium !text-n-slate-12 !shadow-none focus:!ring-0 placeholder:!text-n-slate-10"
              :placeholder="t('HUB.SEARCH_PLACEHOLDER')"
              :aria-label="t('HUB.SEARCH_ARIA')"
            />
          </label>
        </form>
        <div
          v-if="searchTerm"
          class="absolute inset-x-0 top-[calc(100%+6px)] z-30 overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-lg"
          data-testid="hub-search-results"
        >
          <RouterLink
            v-for="hit in destinationHits"
            :key="hit.key"
            :to="destinationPath(hit)"
            class="flex min-h-11 items-center gap-3 px-4 py-2 text-sm hover:bg-n-alpha-1"
            @click="query = ''"
          >
            <span
              class="size-5 shrink-0"
              :class="hit.icon"
              aria-hidden="true"
            />
            <span class="min-w-0 flex-1 truncate">{{ hit.label }}</span>
            <span class="shrink-0 text-xs text-n-slate-11">{{
              hit.worldLabel
            }}</span>
          </RouterLink>
          <RouterLink
            :to="contentSearchPath"
            class="flex min-h-11 items-center gap-3 border-t border-n-weak px-4 py-2 text-sm font-semibold hover:bg-n-alpha-1"
            data-testid="hub-search-content"
            @click="query = ''"
          >
            <span
              class="i-ph-magnifying-glass size-5 shrink-0"
              aria-hidden="true"
            />
            <span class="min-w-0 flex-1 truncate">{{
              t('HUB.SEARCH_CONTENT', { query: searchTerm })
            }}</span>
          </RouterLink>
        </div>
      </div>

      <p
        v-if="!worlds.length"
        class="rounded-xl border border-dashed border-n-weak p-6 text-sm text-n-slate-11"
      >
        {{ t('WORKSPACE_V2.EMPTY') }}
      </p>

      <div
        class="hub-mosaic !grid-cols-1 !grid-rows-none !gap-3.5 md:!grid-cols-[1.25fr_1fr]"
      >
        <div
          v-if="worldById('attendance')"
          class="hub-panel hub-panel--attendance hub-panel--tall min-w-0 !row-auto !min-h-[250px] !bg-hub-attendance !bg-[length:190%_190%,175%_175%,200%_200%,220%_220%] !text-white motion-safe:animate-hub-attendance [&_.hub-panel-tag]:!text-white [&_.hub-panel-sub]:!text-white [&_.hub-panel-go]:!text-white [&_.hub-panel-ctx]:!mt-3 [&_.hub-panel-ctx]:!border-t [&_.hub-panel-ctx]:!border-white/20 [&_.hub-panel-ctx]:!pt-3 [&_.hub-panel-ctx]:!text-white/90"
          :class="{
            'hub-panel--attention': overview.unassignedCount.value > 0,
          }"
          data-testid="hub-world-attendance"
        >
          <div class="hub-panel-top">
            <span class="hub-panel-tag">
              <span class="i-ph-headset size-4" aria-hidden="true" />
              {{ t('HUB.WORLDS.ATTENDANCE') }}
            </span>
            <span
              v-if="overview.unassignedCount.value > 0"
              class="hub-panel-pill"
            >
              {{
                t(
                  'HUB.CENTRAL.UNASSIGNED_PILL',
                  overview.unassignedCount.value,
                  {
                    count: overview.unassignedCount.value,
                  }
                )
              }}
            </span>
          </div>
          <div>
            <div class="hub-panel-big hub-panel-big--xl">
              {{ overview.allCount.value }}
            </div>
            <div class="hub-panel-sub">
              {{
                t('HUB.CENTRAL.WAITING', overview.allCount.value, {
                  count: overview.allCount.value,
                })
              }}
            </div>
            <div class="hub-panel-ctx">
              {{
                t('HUB.CENTRAL.WITH_YOU', { count: overview.mineCount.value })
              }}
              <template v-if="overview.maxWaitLabel.value">
                ·
                {{
                  t('HUB.CENTRAL.MAX_WAIT', {
                    wait: overview.maxWaitLabel.value,
                  })
                }}
              </template>
            </div>
          </div>
          <div class="hub-panel-foot min-w-0 !flex-wrap !justify-start">
            <RouterLink
              :to="workSurfacePath('attendance')"
              class="hub-cta hub-cta--glow min-h-11 min-w-0 max-w-full"
              data-testid="hub-card-open-conversations"
            >
              {{ t('HUB.OPEN_CONVERSATIONS') }}
            </RouterLink>
            <RouterLink
              :to="areaPath('attendance')"
              class="hub-cta hub-cta--ghost min-h-11 min-w-0 max-w-full !border-white/40 !bg-transparent !text-white"
              data-testid="hub-card-attendance-more"
            >
              {{ t('HUB.MORE_INFO') }}
            </RouterLink>
          </div>
        </div>

        <div
          v-if="worldById('sales')"
          class="hub-panel hub-panel--sales min-w-0"
          :class="{ 'hub-panel--attention': overview.tasksOverdue.value > 0 }"
          data-testid="hub-world-sales"
        >
          <div class="hub-panel-top">
            <span class="hub-panel-tag">
              <span class="i-ph-chart-bar size-4" aria-hidden="true" />
              {{ t('HUB.WORLDS.SALES') }}
            </span>
            <span v-if="overview.tasksOverdue.value > 0" class="hub-panel-pill">
              {{
                t('HUB.STATS.TASKS_OVERDUE', overview.tasksOverdue.value, {
                  count: overview.tasksOverdue.value,
                })
              }}
            </span>
            <span v-else class="hub-panel-tag hub-panel-tag--quiet">
              {{
                t('HUB.LIVE.TASKS_TODAY', overview.tasksToday.value, {
                  count: overview.tasksToday.value,
                })
              }}
            </span>
          </div>
          <div>
            <div class="hub-panel-big">
              {{ overview.money(overview.kanban.value.value) }}
            </div>
            <div class="hub-panel-ctx">
              {{
                t('HUB.HOME.DEALS_OPEN', overview.kanban.value.items ?? 0, {
                  count: count(overview.kanban.value.items),
                })
              }}
              <template v-if="canSee.kanbanReports">
                ·
                {{
                  t('HUB.CENTRAL.WON_MONTH', {
                    value: overview.money(overview.kanban.value.revenue),
                  })
                }}
              </template>
            </div>
          </div>
          <div class="hub-panel-foot min-w-0 !flex-wrap !justify-start">
            <RouterLink
              :to="workSurfacePath('sales')"
              class="hub-cta hub-cta--glow min-h-11 min-w-0 max-w-full"
              data-testid="hub-card-open-kanban"
            >
              {{ t('HUB.OPEN_KANBAN') }}
            </RouterLink>
            <RouterLink
              :to="areaPath('sales')"
              class="hub-cta hub-cta--ghost min-h-11 min-w-0 max-w-full"
              data-testid="hub-card-sales-more"
            >
              {{ t('HUB.MORE_INFO') }}
            </RouterLink>
          </div>
        </div>

        <!-- Central da IA no meio, largura cheia, entre Conversas/Kanban e
             Ações de Marketing/Automações. O mosaico tem posições fixas (a
             ordem salva em Organizar vale para as abas da barra, não para
             este quadro), então nenhuma ordem personalizada é sobrescrita. -->
        <HubAiCenterCard
          v-if="worldById('ai_center')"
          :world="worldById('ai_center')"
          :account-id="accountId"
          :live-meta="liveMetaByWorld.ai_center || ''"
          :link-for="destinationPath"
        />

        <RouterLink
          v-if="worldById('marketing')"
          :to="areaPath('marketing')"
          class="hub-panel hub-panel--marketing"
          data-testid="hub-world-marketing"
        >
          <div class="hub-panel-top">
            <span class="hub-panel-tag">
              <span class="i-ph-megaphone size-4" aria-hidden="true" />
              {{ t('HUB.WORLDS.MARKETING') }}
            </span>
            <span
              v-if="canSee.campaigns"
              class="hub-panel-tag hub-panel-tag--quiet"
            >
              {{
                t(
                  'HUB.HOME.SCHEDULED_COUNT',
                  overview.campaigns.value.scheduled ?? 0,
                  {
                    count: count(overview.campaigns.value.scheduled),
                  }
                )
              }}
            </span>
          </div>
          <div v-if="canSee.campaigns">
            <div class="hub-panel-big">
              {{ count(overview.campaigns.value.active) }}
            </div>
            <div class="hub-panel-ctx">
              {{
                t(
                  'HUB.CENTRAL.RUNNING_ACTIONS',
                  overview.campaigns.value.active ?? 0,
                  {
                    count: count(overview.campaigns.value.active),
                  }
                )
              }}
            </div>
          </div>
          <div
            v-else
            class="hub-panel-ctx"
            data-testid="hub-world-marketing-description"
          >
            {{ worldDescription('marketing') }}
          </div>
          <div class="hub-panel-foot">
            <span class="hub-panel-go">
              {{ t('HUB.CENTRAL.NEW_ACTION') }}
              <span class="i-ph-arrow-right size-4" aria-hidden="true" />
            </span>
          </div>
        </RouterLink>

        <RouterLink
          v-if="worldById('automation')"
          :to="areaPath('automation')"
          class="hub-panel hub-panel--automation hub-panel--wide !col-auto !flex-col !items-stretch"
          :class="{
            'hub-panel--attention': overview.flows.value.attention > 0,
          }"
          data-testid="hub-world-automation"
        >
          <div class="min-w-0 flex-1">
            <div class="hub-panel-top">
              <span class="hub-panel-tag">
                <span class="i-ph-sparkle size-4" aria-hidden="true" />
                {{ t('HUB.WORLDS.AUTOMATION') }}
              </span>
              <span
                v-if="canSee.flows && overview.flows.value.attention > 0"
                class="hub-panel-pill"
              >
                {{
                  t(
                    'HUB.CENTRAL.ATTENTION_PILL',
                    overview.flows.value.attention,
                    {
                      count: overview.flows.value.attention,
                    }
                  )
                }}
              </span>
            </div>
            <template v-if="canSee.flows">
              <div class="hub-panel-big">
                {{ count(overview.flows.value.active) }}
              </div>
              <div class="hub-panel-ctx">
                {{
                  t(
                    'HUB.CENTRAL.FLOWS_RUNNING',
                    overview.flows.value.active ?? 0,
                    {
                      count: count(overview.flows.value.active),
                    }
                  )
                }}
              </div>
            </template>
            <div
              v-else
              class="hub-panel-ctx"
              data-testid="hub-world-automation-description"
            >
              {{ worldDescription('automation') }}
            </div>
          </div>
          <div class="hub-panel-side">
            <span class="hub-cta hub-cta--ghost">{{
              canSee.flows ? t('HUB.CENTRAL.SEE_FLOWS') : t('HUB.MORE_INFO')
            }}</span>
            <span
              v-if="overview.flows.value.attentionName"
              class="hub-panel-hint"
            >
              {{
                t('HUB.CENTRAL.CHECK_FLOW', {
                  name: overview.flows.value.attentionName,
                })
              }}
            </span>
          </div>
        </RouterLink>
      </div>

      <!-- Mesmo lugar do Hub clássico: depois dos cartões, lido por escolha.
           O componente se esconde sozinho para quem não é administrador. -->
      <AvisoUpgrade data-testid="hub-central-aviso-upgrade" />

      <nav class="hub-central-links" :aria-label="t('HUB.HOME.LINKS_LABEL')">
        <RouterLink
          v-if="isAdmin"
          :to="routePath('account_overview_reports')"
          class="hub-central-link"
        >
          <span class="i-ph-chart-bar size-4" aria-hidden="true" />
          {{ t('HUB.FOOTER_REPORTS') }}
        </RouterLink>
        <RouterLink
          v-if="isAdmin"
          :to="routePath('settings_menu')"
          class="hub-central-link"
          data-testid="hub-central-settings"
        >
          <span class="i-ph-gear size-4" aria-hidden="true" />
          {{ t('HUB.FOOTER_SETTINGS') }}
        </RouterLink>
        <RouterLink :to="routePath('release_notes')" class="hub-central-link">
          <span class="i-ph-sparkle size-4" aria-hidden="true" />
          {{ t('HUB.DESTINATIONS.RELEASE_NOTES') }}
        </RouterLink>
        <button
          type="button"
          class="hub-central-link"
          data-testid="hub-central-organize"
          @click="openOrganize"
        >
          <span class="i-ph-sliders-horizontal size-4" aria-hidden="true" />
          {{ t('WORKSPACE_V2.ORGANIZE') }}
        </button>
      </nav>
    </div>
  </div>
</template>

<style>
/* Mosaico da Central. Tokens, botões e cores das portas vêm de hubDesign.css
   (classe hub-v3 na raiz). */
.hub-mosaic {
  display: grid;
  grid-template-columns: 1.25fr 1fr;
  grid-template-rows: 230px 230px;
  gap: 14px;
}
.hub-panel {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
  padding: 22px 24px;
  border-radius: 18px;
  border: 1px solid var(--hv-line);
  background: var(--hv-el);
  color: var(--hv-fg);
  text-decoration: none;
  transition:
    transform 200ms cubic-bezier(0.16, 1, 0.3, 1),
    border-color 150ms ease-out;
}
.hub-panel::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: var(--hv-tint);
}
.hub-panel > * {
  position: relative;
}
.hub-panel:hover {
  border-color: var(--hv-line2);
}
@media (prefers-reduced-motion: no-preference) {
  .hub-panel:hover {
    transform: translateY(-2px);
  }
}
.hub-panel:focus-visible {
  outline: 2px solid var(--hv-accent);
  outline-offset: 2px;
}
.hub-panel--tall {
  grid-row: span 2;
}
.hub-panel--wide {
  grid-column: 1 / -1;
  grid-row: auto;
  flex-direction: row;
  align-items: center;
  gap: 24px;
  min-height: 170px;
}
.hub-panel--attendance::before {
  background: radial-gradient(70% 60% at 20% 15%, #7c6cff 0%, transparent 62%),
    radial-gradient(
      50% 50% at 90% 90%,
      rgba(124, 108, 255, 0.5),
      transparent 60%
    );
}
.hub-panel--sales::before {
  background: radial-gradient(70% 70% at 15% 20%, #3ea6ff 0%, transparent 62%);
}
.hub-panel--marketing::before {
  background: radial-gradient(70% 70% at 15% 20%, #ff8a4c 0%, transparent 62%);
}
.hub-panel--automation::before {
  background: radial-gradient(70% 70% at 15% 20%, #2dd4bf 0%, transparent 62%);
}
.hub-panel--attention {
  box-shadow:
    0 0 0 1px rgba(245, 181, 63, 0.4),
    0 12px 40px rgba(245, 181, 63, 0.14);
}
.hub-panel-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.hub-panel-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--hv-fg2);
}
.hub-panel-tag--quiet {
  text-transform: none;
  letter-spacing: 0;
}
.hub-panel-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 800;
  color: var(--hv-amber-ink);
  background: var(--hv-amber-bg);
  border-radius: 9999px;
  padding: 3px 9px;
  white-space: nowrap;
}
.hub-panel-big {
  font-size: 52px;
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}
.hub-panel-big--xl {
  font-size: 72px;
}
.hub-panel-sub {
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin-top: 6px;
}
.hub-panel-ctx {
  color: var(--hv-fg2);
  font-size: 14px;
  margin-top: 6px;
}
.hub-panel-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.hub-panel-go {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 800;
  font-size: 13px;
}
.hub-panel-go :last-child {
  transition: transform 220ms cubic-bezier(0.16, 1, 0.3, 1);
}
.hub-panel:hover .hub-panel-go :last-child {
  transform: translateX(3px);
}
.hub-panel-side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  flex: 0 0 auto;
}
.hub-panel-hint {
  font-size: 12px;
  color: var(--hv-fg2);
  text-align: right;
}
.hub-central-links {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin-top: 18px;
}
.hub-central-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 0 4px;
  border: 0;
  background: transparent;
  color: var(--hv-fg3);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
}
.hub-central-link:hover {
  color: var(--hv-fg);
}
@media (max-width: 860px) {
  .hub-mosaic {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
  }
  .hub-panel--tall {
    grid-row: auto;
    min-height: 220px;
  }
  .hub-panel--wide {
    flex-direction: column;
    align-items: stretch;
  }
  .hub-panel-side {
    align-items: flex-start;
  }
  .hub-panel-big--xl {
    font-size: 56px;
  }
}
</style>
